import React, { useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { TxSelectors } from "src/redux/Tx/selectors";
import { TxActions } from "src/redux/Tx/actions";
import { TopBarSelectors } from "src/redux/TopBar/selectors";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { Tx } from "src/redux/Tx/types";
import {
    getFromLocalUserStorage,
    saveLocalUserStorage,
} from "src/utils/localStorage";
import { AvailabilitySelectors } from "src/redux/Availability/selectors";

import CollapsibleMenu from "src/components/common/CollapsibleMenu";
import CPTYList from "./CPTYList";
import AcceptanceCriteria from "./AcceptanceCriteria";
import OfferDefaults from "./OfferDefaults";
import RequestDefaults from "./RequestDefaults";
import Button from "src/components/common/Button";
import { ROLE, VIEWS } from "src/redux/TopBar/constants";

import styles from "./styles.module.scss";

export default ({ handleSend, type, onCancel, saveNeedRow }) => {
    const isReadyToSubmit =
        type !== VIEWS.AVAILABILITY
            ? useSelector(TxSelectors.TxInitIsReady)
            : useSelector(AvailabilitySelectors.AvailabilityTakeIsReady);
    const role = useSelector(TopBarSelectors.Role);

    return (
        <div className={styles.actions}>
            <div
                className={[
                    styles.settings,
                    onCancel ? styles.twoButtons : "",
                ].join(" ")}
            >
                {role === ROLE.LENDER ? (
                    <OfferSettings />
                ) : type === VIEWS.AVAILABILITY ? (
                    <TakeSettings />
                ) : (
                    <InitSettings />
                )}
            </div>

            <div
                className={`${styles.submit} ${
                    !isReadyToSubmit ? styles.disabled : ""
                } ${onCancel ? styles.twoButtons : ""}`}
            >
                <Button
                    large
                    onClick={handleSend}
                    dataTestId="submit-tx-initiate"
                    value={
                        onCancel
                            ? "Take Selected Matches"
                            : role === ROLE.LENDER ||
                              type === VIEWS.AVAILABILITY
                            ? "Send"
                            : "Send Msg"
                    }
                    disabled={!isReadyToSubmit}
                />
                {onCancel && (
                    <Button
                        large
                        color="reversedBlack"
                        onClick={onCancel}
                        dataTestId="take-tx-none"
                        value="Take None"
                    />
                )}
            </div>
        </div>
    );
};

const InitSettings = () => {
    const dispatch = useDispatch();
    const [includesAC, setIncludesAC] = useState(
        getFromLocalUserStorage(Tx.AC_STORAGE_TOGGLE).includesAC
    );
    const rowData = useSelector(TxSelectors.TxInitRowData);
    // Limited UI §3 "1-to-many → 1-to-1": borrower picks exactly one
    // counterparty for the request. Mirrors the lender's
    // "Counterparty to Receive Offer" dropdown below
    // (CPTYList multiple={false}). Singular wording in the heading.
    const limitedUi = useSelector(SettingsSelectors.FeatureFlags)["limited-ui"];

    const isPortfolioTx = useMemo(
        () =>
            rowData &&
            rowData.length > 0 &&
            rowData.some((row) => row.parentId !== undefined),
        [rowData]
    );

    return (
        <React.Fragment>
            <CollapsibleMenu
                title={
                    limitedUi
                        ? "Select CPTY to receive"
                        : "Select CPTYs to receive request"
                }
                dataTestId="tx-init-cptys"
                // The dropdown branch of CPTYList opens its options
                // downward, outside the menu's bounds. Without
                // allowsOverflow the menu clips them and the dropdown
                // looks empty. The lender's OfferSettings already does
                // this for the same reason; only needed when we're
                // actually rendering the dropdown (limitedUi=true).
                allowsOverflow={limitedUi}
            >
                <CPTYList
                    multiple={!limitedUi}
                    acceptsPartnerCpties={false}
                    acceptsUnregisteredCpties={true}
                />
            </CollapsibleMenu>

            <RequestDefaults isPortfolioTx={isPortfolioTx} />
            <Comment />
            {/* Limited UI §3: hide the Acceptance Criteria collapsible
                section entirely (borrowers on limited-ui can't disable
                or tune the criteria; matching uses draft defaults). */}
            {!isPortfolioTx && !limitedUi && (
                <CollapsibleMenu
                    title="Acceptance Criteria"
                    dataTestId="tx-init-ac"
                    value={includesAC}
                    onChange={() => {
                        saveLocalUserStorage(Tx.AC_STORAGE_TOGGLE, {
                            includesAC: !includesAC,
                        });
                        setIncludesAC(!includesAC);
                        dispatch(
                            TxActions.UpdateAC(
                                !includesAC,
                                getFromLocalUserStorage(Tx.AC_STORAGE_VALUE)
                            )
                        );
                    }}
                >
                    <AcceptanceCriteria on={includesAC} />
                </CollapsibleMenu>
            )}
        </React.Fragment>
    );
};

const OfferSettings = () => {
    return (
        <React.Fragment>
            <CollapsibleMenu
                title="Counterparty to Receive Offer"
                dataTestId="tx-offer-cptys"
                allowsOverflow
            >
                <CPTYList
                    multiple={false}
                    acceptsUnregisteredCpties={true}
                    acceptsPartnerCpties={false}
                />
            </CollapsibleMenu>

            <OfferDefaults />

            <Comment />
        </React.Fragment>
    );
};

const Comment = () => {
    const dispatch = useDispatch();
    const comment = useSelector(TxSelectors.TxInitOfferComment);

    return (
        <CollapsibleMenu title="Comment" dataTestId="tx-offer-comment">
            <label className={styles.comment} aria-label="Comment">
                <textarea
                    aria-label="Comment"
                    value={comment}
                    placeholder="Optional. 100 characters at max."
                    maxLength="100"
                    data-testid="comment-input"
                    onChange={(e) => {
                        let value = e.target.value;
                        if (value.length > 100) {
                            value = value.substring(0, 100);
                        }
                        dispatch(TxActions.SetOfferComment(value));
                    }}
                />
            </label>
        </CollapsibleMenu>
    );
};

const TakeSettings = () => {
    const dispatch = useDispatch();
    const [includesAC, setIncludesAC] = useState(
        getFromLocalUserStorage(Tx.AC_STORAGE_TOGGLE).includesAC
    );
    // Limited UI §3: hide the Acceptance Criteria section entirely
    // on the take-availability flow too (same rationale as InitSettings).
    const limitedUi = useSelector(SettingsSelectors.FeatureFlags)["limited-ui"];

    return (
        <React.Fragment>
            <RequestDefaults type={VIEWS.AVAILABILITY} />

            {!limitedUi && (
                <CollapsibleMenu
                    title="Acceptance Criteria"
                    dataTestId="tx-init-ac"
                    value={includesAC}
                    onChange={() => {
                        saveLocalUserStorage(Tx.AC_STORAGE_TOGGLE, {
                            includesAC: !includesAC,
                        });
                        setIncludesAC(!includesAC);
                        dispatch(
                            TxActions.UpdateAC(
                                !includesAC,
                                getFromLocalUserStorage(Tx.AC_STORAGE_VALUE),
                                VIEWS.AVAILABILITY
                            )
                        );
                    }}
                >
                    <AcceptanceCriteria on={includesAC} type={VIEWS.AVAILABILITY} />
                </CollapsibleMenu>
            )}
        </React.Fragment>
    );
};
